import * as React from "react"
import {
  Provider,
  Root,
  Trigger,
  Content,
  Portal,
} from "@radix-ui/react-tooltip"

import { cn } from "../lib/utils"

const TooltipProvider = Provider

// Enhanced Tooltip that supports both hover (desktop) and tap (touch devices)
const Tooltip = React.forwardRef(function Tooltip({ children, ...props }, ref) {
  const [open, setOpen] = React.useState(false);
  const [isTouchDevice, setIsTouchDevice] = React.useState(false);
  
  React.useEffect(() => {
    // Detect if device supports touch
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);
  
  // For touch devices, use controlled state with tap support
  // For desktop, use default hover behavior
  if (isTouchDevice) {
    return (
      <Root 
        {...props} 
        ref={ref}
        open={open} 
        onOpenChange={setOpen}
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child) && child.type === TooltipTrigger) {
            return React.cloneElement(child, {
              onClick: (e) => {
                e.preventDefault();
                setOpen(!open);
                // Call original onClick if it exists
                if (child.props.onClick) {
                  child.props.onClick(e);
                }
              }
            });
          }
          if (React.isValidElement(child) && child.type === TooltipContent) {
            return React.cloneElement(child, {
              onPointerDownOutside: (e) => {
                setOpen(false);
                // Call original onPointerDownOutside if it exists
                if (child.props.onPointerDownOutside) {
                  child.props.onPointerDownOutside(e);
                }
              }
            });
          }
          return child;
        })}
      </Root>
    );
  }
  
  // For desktop, use default uncontrolled behavior (hover)
  return <Root {...props} ref={ref}>{children}</Root>;
})
Tooltip.displayName = "Tooltip"

const TooltipTrigger = Trigger

const TooltipContent = React.forwardRef(function TooltipContent({ className, sideOffset = 4, ...props }, ref) {
  return (
    <Portal>
      <Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-50 overflow-hidden rounded-md border-0 bg-gray-900 text-white shadow-lg opacity-100 px-3 py-1.5 text-sm animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        {...props}
      />
    </Portal>
  )
})
TooltipContent.displayName = "TooltipContent"

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
